import assert from 'assert';
import React from 'react';
import { isPast } from 'date-fns';
import { serializeError } from 'serialize-error';
import { proxy, useSnapshot } from 'valtio';
import type { Address } from 'viem';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { assertAddress } from '@soundxyz/utils/address';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { ContractMethod } from '@soundxyz/utils/constants/transaction';
import { CollectButton } from '@components/CollectButton/BondingCurve/CollectButton';
import { GiftingButton } from '@components/Gifting/GiftingButton';
import {
  type Action,
  QuantityInput,
  quantityReducer,
  type State,
} from '@components/main-components/inputs/QuantityInput';
import { sendReferralSourceIfPresent } from '@components/modals/Buy/ReferralSource';
import { BuySamEditionModal } from '@components/modals/Buy/Sam/BuySamEditionModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMintReferral } from '@context/mintReferral';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { Transaction } from '@context/transaction';
import { getTransactionResponse } from '@context/transaction/TransactionStatusStore';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  SamCollectInfoFragmentDoc,
  SamSaleInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { useSoundSdk } from '@hooks/useSoundSdk';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { trackClickEvent } from '@lib/analytics';
import { useEstimateSamBuyGas } from '@store/sdk/samReactQuery';
import { chainFromId } from '@utils/chain';
import { SDK_SOUND_ATTRIBUTON_ID } from '@utils/constants';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import { NetworkIndicator } from '../../NetworkIndicator';
import { BondingCurveFaqButton } from './BondingCurveFaqButton';
import { TAB_CHOICES } from './BondingMintComponent';
import { CollectingNotAvailable } from './TextInfo';
import { milestonePriceAndQuantity, totalBuyPriceWithBuffer } from './priceUtils';
import { StyledTabContent } from './shared';
import { TextLabel } from './sharedStyles';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SamSaleInfo on Release {
    id
    samConfig {
      id
      affiliateFeeBPS
      artistFeeBPS
      basePrice
      buyFreezeTime
      goldenEggFeeBPS
      inflectionPoint
      inflectionPrice
      maxSupply
      platformFeeBPS
      platformPerTxFlatFee
    }
    samBuyBufferBpsOverride
    samSellBufferBpsOverride
    saleDetails {
      soundSwapCollected
    }
  }
  fragment SamCollectInfo on Release {
    id
    chainId
    contractAddress
    samAddress
    samConfig {
      maxSupply
      buyFreezeTime
    }

    ...BuyEditionSongDetails
    ...ReleaseReferrableInfo
    ...ReleaseUpdates
    ...SamSaleInfo
    ...ReleaseDetails
  }
`);

// NOTE: in future if we use this some other place, we should pass in the source component for analytics
export const useBuyInfo = ({
  data,
  selectedQuantity,
  giftAddress,
}: {
  data: FragmentType<SamCollectInfoFragmentDoc>;
  selectedQuantity: number;
  giftAddress?: Address | null;
}) => {
  const release = getFragment(SamCollectInfoFragmentDoc, data);
  const { walletAddress, expectMatchingChain, getDeepLink, provider } = useConnectedAuth();
  const { soundClientSigner } = useSoundSdk();

  const logError = useLogError();
  const logInfo = useLogInfo();
  const { openToast } = ToastContext.useContainer();
  const [isBuying, setIsBuying] = React.useState(false);
  const { handleTransaction } = Transaction.useContainer();
  const chainId = release.chainId;
  const { getAffiliate } = useCheckReferrableRelease({
    release,
  });
  const { referral } = useMintReferral();

  const {
    data: {
      saleDetails: { soundSwapCollected },
      samBuyBufferBpsOverride,
    },
  } = useSubscribeRelease({
    release,
  });

  const { samConfig } = getFragment(SamSaleInfoFragmentDoc, release);

  assert(samConfig, 'SamConfig is required for buy info.');

  const {
    basePrice,
    inflectionPoint,
    inflectionPrice,
    affiliateFeeBPS,
    artistFeeBPS,
    goldenEggFeeBPS,
    platformFeeBPS,
    platformPerTxFlatFee,
  } = samConfig;

  const valueBeforeTransactionFee = totalBuyPriceWithBuffer({
    basePrice,
    linearPriceSlope: 0n,
    inflectionPoint,
    inflectionPrice,
    currentSupply: soundSwapCollected,
    quantity: selectedQuantity,
    artistFeeBPS,
    goldenEggFeeBPS,
    affiliateFeeBPS,
    bufferOverrideBps: samBuyBufferBpsOverride,
    platformFeeBPS,
  });

  const valueIncludingTransactionFee = valueBeforeTransactionFee + BigInt(platformPerTxFlatFee);

  const { milestonePriceInWei, tokensToMilestone } = milestonePriceAndQuantity({
    basePrice,
    linearPriceSlope: 0n,
    inflectionPoint,
    inflectionPrice,
    currentSupply: soundSwapCollected,
    artistFeeBPS,
    goldenEggFeeBPS,
    affiliateFeeBPS,
    bufferOverrideBps: samBuyBufferBpsOverride,
    platformFeeBPS,
  });

  const { data: gasEstimate } = useEstimateSamBuyGas({
    chainId,
    editionAddress: release.contractAddress,
    maxValue: valueIncludingTransactionFee,
    quantity: selectedQuantity,
  });

  const affiliate = getAffiliate(referral) || NULL_ADDRESS;
  const minterAddress = release.samAddress || '';

  const toAddress = !!giftAddress ? giftAddress : walletAddress ?? '';

  const handleBuy = useStableCallback(async () => {
    if (!walletAddress) {
      return logError({
        action: MINTING_ERROR_ACTIONS.SIGNER_NOT_FOUND_ERROR,
        error: new Error('Signer not found when trying to buy from edition contract.'),
        message: 'Signer not found when trying to buy from edition contract.',
        toast: 'Wallet not found. Try reconnecting wallet.',
        errorType: ERROR_TYPE.SIGNER_NOT_FOUND,
        pillar: PILLARS.MINTING,
        level: 'info',
        isArtistOnlyPage: false,
      });
    }

    trackClickEvent({
      event: 'Pay with ETH Clicked',
      properties: {
        associationId: release.id,
        associationType: 'Release',
        quantity: selectedQuantity,
        chainId,
        affiliateAddress: affiliate.toLowerCase(),
        minterAddress,
        // In future we should pass in the source component for analytics
        sourceComponent: 'Collect Modal',
        mintTo: toAddress,
      },
    });
    const chain = expectMatchingChain(chainId);

    if (chain.stop) return;

    setIsBuying(true);

    try {
      if (chain.switching) await chain.switching;
    } catch (error) {
      openToast({
        variant: 'error',
        text: 'Please switch to ethereum mainnet',
      });
      setIsBuying(false);
      return;
    }

    try {
      const transactionFn = async () => {
        if (selectedQuantity === 0) {
          throw Error('Select a quantity to collect.');
        }

        const signer = soundClientSigner(chainId);

        assertAddress(release.contractAddress);
        const { contract } = signer.edition.sam({
          editionAddress: release.contractAddress,
        });

        assertAddress(affiliate);

        const toAddress = !!giftAddress ? giftAddress : walletAddress;

        const mintResult = await contract.buy({
          quantity: selectedQuantity,
          maxTotalValue: valueIncludingTransactionFee,
          affiliate,
          attributonId: SDK_SOUND_ATTRIBUTON_ID,

          gas: gasEstimate?.gasLimit,
          chain: chainFromId(chainId),

          mintTo: toAddress,
        });

        sendReferralSourceIfPresent({
          txHash: mintResult.transactionHash,
          chainId,
          referral,
        });

        return getTransactionResponse({
          hash: mintResult.transactionHash,
          chainId,
          provider: provider(chainId),
        });
      };

      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      const deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }

      logInfo({
        action: MINTING_INFO_ACTIONS.SAM_MINT_EDITION,
        message: !!giftAddress ? 'Gifting SAM edition' : 'Minting SAM edition',
        data: {
          releaseId: release.id,
          chainId,
          minterAddress,
          giftAddress,
        },
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
      });

      const status = await handleTransaction({
        contractMethod: ContractMethod.SAM__BUY_EDITION,
        transactionFn,
        releaseId: release.id,
        chainId,
        openProcessingModal: true,
      });
      if (status) {
        trackClickEvent({
          event: 'Pay with ETH Success',
          properties: {
            associationId: release.id,
            associationType: 'Release',
            quantity: selectedQuantity,
            chainId,
            affiliateAddress: affiliate.toLowerCase(),
            minterAddress,
            // In future we should pass in the source component for analytics
            sourceComponent: 'Collect Modal',
            mintTo: toAddress,
            isGifted: !!giftAddress,
          },
        });
      }
    } catch (err) {
      const error = serializeError(err);
      logError({
        action: MINTING_ERROR_ACTIONS.SAM_MINT_EDITION_ERROR,
        error,
        errorType: ERROR_TYPE.SAM_MINT_FAILED,
        toast: 'Error while collecting SAM edition',
        message: 'Error while collecting SAM edition',
        level: 'fatal',
        indexedTags: {
          releaseId: release.id,
          selectedQuantity: selectedQuantity,
          chainId,
        },
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
      });
    } finally {
      setIsBuying(false);
    }
  });

  return {
    valueBeforeTransactionFee,
    platformPerTxFlatFee,
    valueIncludingTransactionFee,
    handleBuy,
    isBuying,
    milestonePriceInWei,
    tokensToMilestone,
    getAffiliate,
    gasEstimate,
  };
};

const quantityState = proxy<Record<string, State>>({});
export const useSamBuyQuantityReducer = ({
  releaseId,
  value,
  error,
}: {
  releaseId: string;
  value?: number;
  error?: boolean;
}) => {
  const stateMap = useSnapshot(quantityState);

  const state = (() => {
    const lookupState = stateMap?.[releaseId];
    if (lookupState) return lookupState;
    quantityState[releaseId] = { value: value || 0, error: error || false };
    return { value: value || 0, error: error || false };
  })();

  return {
    state,
    dispatch: (dispatch: Action) => {
      const updatedState = quantityReducer(state, dispatch);
      quantityState[releaseId] = updatedState;
    },
  };
};

export function CollectInfo({
  data,
  collectButtonSource,
}: {
  data: FragmentType<SamCollectInfoFragmentDoc>;
  collectButtonSource: CollectButtonSource;
}) {
  const [hasError, setHasError] = React.useState(false);
  const { openModal } = ModalContext.useContainer();
  const { isAuthenticatedWithAPI } = useAuth();

  const releaseData = getFragment(SamCollectInfoFragmentDoc, data);

  const samConfig = releaseData.samConfig;

  const {
    data: {
      saleDetails: { soundSwapCollected },
    },
  } = useSubscribeRelease({
    release: releaseData,
  });

  /**
   * There is technically max uint32 as max supply, but we don't want to show that
   * our UI we limit users to buy 1k at a time
   * so we are just adjusting the max supply so we disable the + button when that amount is reached
   */
  const maxMintableByUser = Math.min((samConfig?.maxSupply || 0) - (soundSwapCollected || 0), 1000);

  const samIsFrozen = samConfig?.buyFreezeTime ? isPast(samConfig.buyFreezeTime) : false;

  const samDisabled = !maxMintableByUser || samIsFrozen;
  /**
   * Disable collecting if:
   * - there is an error
   * - max supply is reached
   * - sam is frozen
   */
  const disableCollecting = hasError || samDisabled;

  const { state, dispatch } = useSamBuyQuantityReducer({
    releaseId: releaseData.id,
    // when collecting is disabled we want to show 0
    value: disableCollecting ? 0 : 1,
    error: false,
  });

  const { isBuying, valueBeforeTransactionFee, milestonePriceInWei, tokensToMilestone } =
    useBuyInfo({
      data,
      selectedQuantity: state.value === '' ? 0 : state.value,
    });

  const priceEthString = `${formatWeiToEthForDisplay({
    weiValue: valueBeforeTransactionFee.toString(),
  })} ETH`;

  return (
    <StyledTabContent value={TAB_CHOICES.COLLECT}>
      {samDisabled ? (
        <CollectingNotAvailable />
      ) : (
        <>
          <div className="flex min-h-[42px] w-full items-center justify-between py-1">
            <TextLabel>Quantity</TextLabel>
            <QuantityInput
              state={state}
              // when collecting is disabled we want to show 0
              min={disableCollecting ? 0 : 1}
              dispatch={dispatch}
              max={maxMintableByUser}
              onError={error => {
                switch (error) {
                  case 'SET':
                    setHasError(true);
                    break;
                  default:
                    setHasError(false);
                }
              }}
            />
          </div>
          <div className="flex min-h-[42px] w-full items-center justify-between py-1">
            <TextLabel>
              <h3>Price</h3>
            </TextLabel>
            <div className="flex items-center gap-2">
              <NetworkIndicator chainId={releaseData.chainId} size="md" />
              <div className="text-title-m font-medium leading-[unset]">{priceEthString}</div>
            </div>
          </div>

          <BondingCurveFaqButton
            milestonePriceInWei={milestonePriceInWei}
            tokensToMilestone={tokensToMilestone}
          />
        </>
      )}
      <div className="mt-2 flex w-full gap-2 [&_a]:w-full">
        <CollectButton
          source={collectButtonSource}
          releaseId={releaseData.id}
          onBuy={() => {
            openModal(ModalType.SAM_WITH_CC, {
              body: (
                <BuySamEditionModal
                  key="buy-sam-edition-modal"
                  maxMintableByUser={maxMintableByUser}
                  setHasError={setHasError}
                  data={data}
                  disableCollecting={disableCollecting}
                  openGiftingDropdown={false}
                />
              ),
            });
          }}
          loading={isBuying}
          disabled={disableCollecting}
        />
        {isAuthenticatedWithAPI && (
          <GiftingButton
            releaseId={releaseData.id}
            sourceComponent="Mint Component"
            onClick={() => {
              openModal(ModalType.RELEASE_CHECKOUT_GIFT, {
                body: (
                  <BuySamEditionModal
                    key="gift-sam-edition-modal"
                    maxMintableByUser={maxMintableByUser}
                    setHasError={setHasError}
                    data={data}
                    disableCollecting={disableCollecting}
                    openGiftingDropdown
                  />
                ),
              });
            }}
            disabled={disableCollecting}
            size="XL"
          />
        )}
      </div>
    </StyledTabContent>
  );
}
